import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Checkbox, Col, Form, Row } from 'antd';

import { CountryCallingCodeSelect, CountrySelect, StateSelect } from '@/views/screens/common';

import { WEEKS_DAYS } from '../../../../contants/common';
import { saveShipmentAddressBookInProgress } from '../../../../redux/shipment/ShipmentActions';
import { valueToString } from '../../../../utilz';
import { formateWithMoment } from '../../../../utilz/dates';
import { routeFileSelector } from '../../../../utilz/i18n';
import Button from '../../../components/Buttons/Button';
import FilterCard from '../../../components/Cards/FilterCard';
import FormInput from '../../../components/Inputs/FormInput';
import FormSelectBox from '../../../components/Inputs/FormSelectBox';
import FormTextArea from '../../../components/Inputs/FormTextArea';
import FormTimePicker from '../../../components/Inputs/FormTimePicker';
import HiddenFormInput from '../../../components/Inputs/HiddenFormInput';
import FormLabel from '../../../components/Labels/FormLabel';
import AppointmentSelector from '../../../components/Selectors/AppointmentSelector';
import PostalCodeInput from '../../../components/Selectors/PostalCodeInput';
import SaveCancelButtons from '../../Shipment/components/SaveCancelButtons';

const AddressBookCard = ({
  defaultDetails = {},
  onSelectAddressBook = () => {},
  customerKey,
  currentUserId,
  index,
  handleDelete,
  withSelection = true,
  handleEdit,
  onCheck,
  isChecked,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  let editMode = Form.useWatch('editMode', form);
  editMode = Boolean(editMode);
  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const setEditMode = (val) => {
    form.setFieldValue('editMode', val);
  };

  const handleCountryChange = (data) => {
    form.setFieldValue('countryCallingCode', `+${data?.countryCallingCode}`);
  };
  const countryCode = Form.useWatch('countryCode', form);

  const handleZipDetails = (data) => {
    form.setFieldValue('city', data?.city);
    form.setFieldValue('state', data?.state);
    form.setFieldValue('zip', data?.zip);
    form.setFieldValue('countryCode', data?.countryCode);
  };

  const handleSubmit = ({
    fromOperationDay,
    toOperationDay,
    fromOperationHours,
    toOperationHours,
    countryCallingCode,
    phone,
    ...data
  }) => {
    const payload = {
      ...data,
      callback: () => setEditMode(false),
      createdBy: currentUserId,
      customerKey,
      daysOfWeek: `${valueToString(fromOperationDay)}-${valueToString(toOperationDay)}`,
      hoursOfOperation: `${formateWithMoment(
        fromOperationHours,
        'hh:mm A',
      )}-${formateWithMoment(toOperationHours, 'hh:mm A')}`,
      phone: `${valueToString(countryCallingCode)}-${valueToString(phone)}`,
    };
    // TODO: migrate to useQuery
    handleEdit ? handleEdit(payload, payload.callback) : dispatch(saveShipmentAddressBookInProgress(payload));
  };

  const handleCancel = () => {
    form.setFieldsValue({ ...defaultDetails, editMode: false });
  };

  return (
    <Form className="mb-15" form={form} initialValues={{ ...defaultDetails }} onFinish={handleSubmit}>
      <FilterCard
        shadow={false}
        showBorder
        className="background-white"
        customHeader={
          <div className="w-100">
            <Row className="flex-nowrap" align="middle" gutter={30}>
              {onCheck && (
                <Col sm={1}>
                  <Checkbox
                    className="normal-checkbox"
                    onClick={() => onCheck(defaultDetails.locationId)}
                    checked={isChecked}
                  />
                </Col>
              )}
              <Col sm={6}>
                <FormInput
                  name="name"
                  singleLineMode
                  colon
                  editMode={editMode}
                  label={t('translation:default.fields.companyName.label')}
                  margin="m-0"
                />
              </Col>
              <Col sm={6}>
                <FormInput
                  name="searchId"
                  singleLineMode
                  colon
                  editMode={editMode}
                  label={t('translation:default.fields.location.label')}
                  margin="m-0"
                  rules={[
                    {
                      message: t('translation:default.fields.location.message'),
                      required: true,
                    },
                    { message: '', type: 'string' },
                    {
                      max: 13,
                      message: t('translation:default.fields.location.messageMax'),
                    },
                  ]}
                  maxLength={13}
                />
              </Col>
              <Col sm={5}>
                <FormInput
                  name="siteId"
                  singleLineMode
                  colon
                  editMode={editMode}
                  label={t('translation:addressBook.siteId.label')}
                  margin="m-0"
                  rules={[
                    {
                      max: 35,
                      message: t('translation:default.fields.maxLengthMessage', {
                        field: t('translation:addressBook.siteId.label'),
                        length: 35,
                      }),
                    },
                  ]}
                />
              </Col>
              <Col sm={6}>
                <Row align="middle" justify="end">
                  <Col>
                    <Row gutter={30} align="middle">
                      {withSelection && (
                        <Col>
                          <Button
                            disabled={editMode}
                            variant="success"
                            onClick={() => onSelectAddressBook(defaultDetails)}
                          >
                            {t('translation:default.buttons.select')}
                          </Button>
                        </Col>
                      )}
                      <Col>
                        <SaveCancelButtons
                          editMode={editMode}
                          setEditMode={setEditMode}
                          onCancel={handleCancel}
                          handleRemove={() => handleDelete(defaultDetails, index)}
                        />
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        }
      >
        <div className={`background-white px-15 pt-15 ${editMode ? 'pb-15' : 'pb-8'}`}>
          <Row gutter={30}>
            <Col sm={6}>
              <FormTextArea
                name="addr1"
                label={t('translation:default.fields.companyAddress.label')}
                editMode={editMode}
                // biome-ignore lint/style/noMagicNumbers: layout
                rows={editMode ? 5 : 4}
                margin={editMode ? 'mb-12' : 'mb-5'}
                maxLength={100}
              />
              <CountrySelect
                name="countryCode"
                label={t('translation:default.fields.country.label')}
                editMode={editMode}
                rules={[
                  {
                    message: t('translation:default.fields.country.message'),
                    required: true,
                  },
                ]}
                onChange={handleCountryChange}
                margin="m-0"
              />
            </Col>
            <Col sm={6}>
              <PostalCodeInput
                name="zip"
                editMode={editMode}
                countryCode={countryCode}
                handleZipDetails={handleZipDetails}
                rules={[
                  {
                    message: t('translation:default.fields.postalCode.message'),
                    required: true,
                  },
                ]}
                margin="mb-15"
                form={form}
              />
              <StateSelect
                name="state"
                countryCode={countryCode}
                rules={[
                  {
                    message: t('translation:default.fields.state.message'),
                    required: true,
                  },
                ]}
                margin="mb-12"
                editMode={editMode}
              />
              <FormInput
                name="city"
                label={t('translation:default.fields.city.label')}
                editMode={editMode}
                rules={[
                  {
                    message: t('translation:default.fields.city.message'),
                    required: true,
                  },
                ]}
                margin="m-0"
              />
            </Col>
            <Col sm={6}>
              <FormInput
                name="contactName"
                label={t('translation:default.fields.contactName.label')}
                editMode={editMode}
                margin="mb-15"
                maxLength={50}
              />
              <div className=" mb-15">
                <FormLabel>{t('translation:default.fields.contactNumber')}</FormLabel>
                <Row gutter={10} className="mt-2">
                  <Col sm={6}>
                    <CountryCallingCodeSelect
                      margin="mb-0"
                      editMode={editMode}
                      name="countryCallingCode"
                      placeholder={t('translation:default.fields.code')}
                      label=""
                    />
                  </Col>
                  <Col sm={13}>
                    <FormInput
                      margin="mb-0"
                      editMode={editMode}
                      name="phone"
                      placeholder={t('translation:default.fields.number')}
                      inputType="number"
                      maxLength={15}
                    />
                  </Col>
                  <Col sm={5}>
                    <FormInput
                      placeholder={t('translation:parcelExpress.locationForm.ext')}
                      margin="mb-0"
                      editMode={editMode}
                      name="phoneExt"
                      maxLength={10}
                    />
                  </Col>
                </Row>
              </div>
              <FormInput
                name="contactEmail"
                label={t('translation:default.fields.contactEmailAddress.label')}
                editMode={editMode}
                margin="m-0"
                rules={[
                  {
                    message: t('translation:default.notification.email'),
                    type: 'email',
                  },
                ]}
                maxLength={150}
              />
            </Col>
            <Col sm={6}>
              <AppointmentSelector
                label={t('translation:default.fields.appointmentType.label')}
                name="appointmentType"
                margin={`${editMode ? 'mb-12' : 'mb-13'}`}
                editMode={editMode}
              />
              <Row gutter={10} className={`w-100 mb-2 ${editMode ? 'mb-12' : 'mb-8'}`}>
                <Col sm={24} className={editMode ? 'mb-2' : ''}>
                  <FormLabel className="form-input-label">{t('translation:default.fields.daysOfWeek')}</FormLabel>
                </Col>
                <Col sm={12}>
                  <FormSelectBox
                    options={WEEKS_DAYS}
                    editMode={editMode}
                    singleLineMode
                    label={t('translation:default.fields.from.label')}
                    colon
                    name="fromOperationDay"
                    margin="mb-0"
                    placeholder={t('translation:default.fields.from.placeholder')}
                  />
                </Col>
                <Col sm={12}>
                  <FormSelectBox
                    options={WEEKS_DAYS}
                    editMode={editMode}
                    singleLineMode
                    label={t('translation:default.fields.to.label')}
                    colon
                    name="toOperationDay"
                    placeholder={t('translation:default.fields.to.placeholder')}
                    margin="mb-0"
                  />
                </Col>
              </Row>
              <Row gutter={10} className="w-100">
                <Col sm={24} className={editMode ? 'mb-2' : ''}>
                  <FormLabel className="form-input-label">
                    {t('translation:default.fields.hoursOfOperations')}
                  </FormLabel>
                </Col>
                <Col sm={12}>
                  <FormTimePicker
                    label={t('translation:default.fields.from.label')}
                    colon
                    editMode={editMode}
                    singleLineMode
                    name="fromOperationHours"
                    margin="m-0"
                  />
                </Col>
                <Col sm={12}>
                  <FormTimePicker
                    label={t('translation:default.fields.to.label')}
                    colon
                    editMode={editMode}
                    singleLineMode
                    name="toOperationHours"
                    margin="m-0"
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </div>
      </FilterCard>
      <HiddenFormInput name="editMode" />
      <HiddenFormInput name="locationId" />
    </Form>
  );
};

export default AddressBookCard;
